import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  CheckSquare, Plus, Edit2, Trash2, Calendar, AlertCircle, Clock,
  User, ChevronDown, ChevronRight, Folder, Target, Layers,
  Users as UsersIcon, Check, X, Filter, LayoutGrid, CalendarDays,
  ChevronLeft, AlertTriangle, Truck, Eye
} from 'lucide-react';
import { API_URL } from '../config';

// ── Statuts des tâches ──────────────────────────────────────────────────
const TASK_STATUSES = [
  { value: 'À faire',   label: 'À faire',   bg: 'bg-elevated-1',   text: 'text-text-secondary',   dot: 'bg-gray-400',   badge: 'bg-gray-200 text-text-secondary' },
  { value: 'En cours',  label: 'En cours',   bg: 'bg-blue-100 dark:bg-blue-900/40',   text: 'text-blue-700',   dot: 'bg-blue-500',   badge: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
  { value: 'Terminé',   label: 'Terminé',    bg: 'bg-green-100 dark:bg-green-900/40',  text: 'text-green-700',  dot: 'bg-green-500',  badge: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' },
  { value: 'En retard', label: 'En retard',  bg: 'bg-red-100 dark:bg-red-900/40',    text: 'text-red-700',    dot: 'bg-red-500',    badge: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' },
  { value: 'Annulée',   label: 'Annulée',    bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700', dot: 'bg-orange-400', badge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' },
];

const getStatusStyle = (status) => TASK_STATUSES.find(s => s.value === status) || TASK_STATUSES[0];

// ── Heures pour la vue calendrier ───────────────────────────────────────
const CALENDAR_HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6h → 21h

const PERSON_COLORS = [
  { bg: 'bg-blue-100 dark:bg-blue-900/40', border: 'border-blue-400', text: 'text-blue-800 dark:text-blue-200', header: 'bg-blue-500' },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/40', border: 'border-emerald-400', text: 'text-emerald-800 dark:text-emerald-200', header: 'bg-emerald-500' },
  { bg: 'bg-purple-100 dark:bg-purple-900/40', border: 'border-purple-400', text: 'text-purple-800 dark:text-purple-200', header: 'bg-purple-500' },
  { bg: 'bg-amber-100 dark:bg-amber-900/40', border: 'border-amber-400', text: 'text-amber-800 dark:text-amber-200', header: 'bg-amber-500' },
  { bg: 'bg-rose-100 dark:bg-rose-900/40', border: 'border-rose-400', text: 'text-rose-800 dark:text-rose-200', header: 'bg-rose-500' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/40', border: 'border-cyan-400', text: 'text-cyan-800 dark:text-cyan-200', header: 'bg-cyan-500' },
  { bg: 'bg-orange-100 dark:bg-orange-900/40', border: 'border-orange-400', text: 'text-orange-800 dark:text-orange-200', header: 'bg-orange-500' },
  { bg: 'bg-indigo-100 dark:bg-indigo-900/40', border: 'border-indigo-400', text: 'text-indigo-800 dark:text-indigo-200', header: 'bg-indigo-500' },
];

const Taches = ({ employees = [], currentUser, services = [], authToken, managerServices = [], vehicules = [], schedules = [] }) => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [objectifs, setObjectifs] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [expandedTasks, setExpandedTasks] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showSubtaskModal, setShowSubtaskModal] = useState(null);

  // Filtres
  const [filterPerson, setFilterPerson] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [filterDate, setFilterDate] = useState('');

  // Vue : 'kanban', 'calendar' ou 'team'
  const [viewMode, setViewMode] = useState(() => {
    if (currentUser && ['Patron','Super Manager','Manager'].includes(currentUser.role_name)) return 'team';
    return 'kanban';
  });
  const [calendarDate, setCalendarDate] = useState(new Date().toISOString().split('T')[0]);
  const [calendarSubView, setCalendarSubView] = useState('jour'); // jour | semaine

  // Vue équipe : sous-vue jour/semaine/mois
  const [teamSubView, setTeamSubView] = useState('jour');
  const [teamDate, setTeamDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedWeekAccordion, setExpandedWeekAccordion] = useState({}); // { empId: true/false }

  // Alerte véhicule
  const [vehicleAlert, setVehicleAlert] = useState(null);

  const [formData, setFormData] = useState({
    title: '', description: '', assigned_to: '', assigned_service_id: '',
    project_id: '', objectif_id: '', priority: 'Moyenne', due_date: '',
    status: 'À faire', resultat_attendu: '', assignType: 'person',
    scheduled_date: '', scheduled_start_time: '', scheduled_end_time: '',
    vehicule_id: ''
  });

  // ── Services du manager (multi-services) ──────────────────────────────
  const managerServiceIds = useMemo(() => {
    if (!currentUser || currentUser.role_name !== 'Manager') return [];
    const ids = managerServices
      .filter(ms => ms.manager_id === (currentUser.employee_id || currentUser.id))
      .map(ms => ms.service_id);
    // Inclure le service natif du manager
    if (currentUser.service_id && !ids.includes(parseInt(currentUser.service_id))) {
      ids.push(parseInt(currentUser.service_id));
    }
    return ids;
  }, [currentUser, managerServices]);

  // Charger les tâches
  const loadTasks = async () => {
    setLoading(true);
    try {
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      const res = await fetch(`${API_URL}/tasks`, { headers });
      const data = await res.json();
      setTasks(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); setTasks([]); }
    setLoading(false);
  };

  const loadProjectsAndObjectifs = async () => {
    try {
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API_URL}/projects`, { headers }).catch(() => ({ json: () => [] })),
        fetch(`${API_URL}/objectifs`, { headers }).catch(() => ({ json: () => [] })),
        fetch(`${API_URL}/services`, { headers }).catch(() => ({ json: () => [] }))
      ]);
      const [d1, d2, d3] = await Promise.all([r1.json(), r2.json(), r3.json()]);
      setProjects(Array.isArray(d1) ? d1 : []);
      setObjectifs(Array.isArray(d2) ? d2 : []);
      setServicesList(Array.isArray(d3) ? d3 : (services || []));
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadTasks(); loadProjectsAndObjectifs(); }, []);

  // ── Vérification conflits véhicule ──────────────────────────────────────
  const checkVehicleConflicts = useCallback(async (vId, date, excludeTaskId) => {
    if (!vId || !date) { setVehicleAlert(null); return; }
    try {
      let url = `${API_URL}/tasks/vehicle-conflicts?vehicule_id=${vId}&date=${date}`;
      if (excludeTaskId) url += `&exclude_task_id=${excludeTaskId}`;
      const headers = authToken ? { 'Authorization': `Bearer ${authToken}` } : {};
      const res = await fetch(url, { headers });
      const conflicts = await res.json();
      if (conflicts && conflicts.length > 0) {
        setVehicleAlert(conflicts);
      } else {
        setVehicleAlert(null);
      }
    } catch { setVehicleAlert(null); }
  }, [authToken]);

  // Déclencher la vérification quand véhicule ou date change dans le formulaire
  useEffect(() => {
    const vId = formData.vehicule_id;
    const date = formData.scheduled_date || formData.due_date;
    if (vId && date) {
      checkVehicleConflicts(vId, date, editingTask);
    } else {
      setVehicleAlert(null);
    }
  }, [formData.vehicule_id, formData.scheduled_date, formData.due_date, editingTask, checkVehicleConflicts]);

  // CRUD
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = editingTask ? `${API_URL}/tasks/${editingTask}` : `${API_URL}/tasks`;
      if (!formData.due_date) {
        alert('La date limite est obligatoire');
        setLoading(false);
        return;
      }
      const payload = {
        title: formData.title, description: formData.description,
        assigned_to: formData.assignType === 'person' ? (formData.assigned_to || null) : null,
        assigned_service_id: formData.assignType === 'service' ? (formData.assigned_service_id || null) : null,
        project_id: formData.project_id || null, objectif_id: formData.objectif_id || null,
        priority: formData.priority, due_date: formData.due_date || null,
        status: formData.status, resultat_attendu: formData.resultat_attendu || null,
        scheduled_date: formData.scheduled_date || null,
        scheduled_start_time: formData.scheduled_start_time || null,
        scheduled_end_time: formData.scheduled_end_time || null,
        vehicule_id: formData.vehicule_id || null,
      };
      if (!editingTask) payload.assigned_by = currentUser?.employee_id || currentUser?.id || 1;
      const res = await fetch(url, { method: editingTask ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }, body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Erreur');
      }
      await loadTasks();
      closeModal();
    } catch (e) { alert('Erreur : ' + e.message); }
    setLoading(false);
  };

  const handleSubmitSubtask = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        title: formData.title, description: formData.description,
        assigned_to: formData.assignType === 'person' ? (formData.assigned_to || null) : null,
        assigned_service_id: formData.assignType === 'service' ? (formData.assigned_service_id || null) : null,
        parent_task_id: showSubtaskModal,
        assigned_by: currentUser?.employee_id || currentUser?.id || 1,
        priority: formData.priority, due_date: formData.due_date || null, status: 'À faire',
        scheduled_date: formData.scheduled_date || null,
        scheduled_start_time: formData.scheduled_start_time || null,
        scheduled_end_time: formData.scheduled_end_time || null,
        vehicule_id: formData.vehicule_id || null,
      };
      const res = await fetch(`${API_URL}/tasks`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }, body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Erreur');
      }
      await loadTasks();
      setShowSubtaskModal(null);
      closeModal();
    } catch (e) { alert('Erreur : ' + e.message); }
    setLoading(false);
  };

  const changeStatus = async (taskId, newStatus) => {
    try {
      await fetch(`${API_URL}/tasks/${taskId}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ status: newStatus })
      });
      await loadTasks();
    } catch { alert('Erreur changement statut'); }
  };

  // Toggle checkbox rapide (Terminé ↔ À faire)
  const toggleTaskDone = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'Terminé' ? 'À faire' : 'Terminé';
    // Optimistic UI
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    try {
      await fetch(`${API_URL}/tasks/${taskId}/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
        body: JSON.stringify({ status: newStatus })
      });
    } catch {
      // Revert on error
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: currentStatus } : t));
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Supprimer cette tâche (et ses sous-tâches) ?')) return;
    try {
      await fetch(`${API_URL}/tasks/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      await loadTasks();
    } catch { alert('Erreur'); }
  };

  const openEditModal = (task) => {
    setEditingTask(task.id);
    setFormData({
      title: task.title, description: task.description || '',
      assigned_to: task.assigned_to || '', assigned_service_id: task.assigned_service_id || '',
      project_id: task.project_id || '', objectif_id: task.objectif_id || '',
      priority: task.priority || 'Moyenne', due_date: task.due_date ? task.due_date.split('T')[0] : '',
      status: task.status || 'À faire', resultat_attendu: task.resultat_attendu || '',
      assignType: task.assigned_service_id ? 'service' : 'person',
      scheduled_date: task.scheduled_date || '',
      scheduled_start_time: task.scheduled_start_time || '',
      scheduled_end_time: task.scheduled_end_time || '',
      vehicule_id: task.vehicule_id || '',
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false); setEditingTask(null); setShowSubtaskModal(null); setVehicleAlert(null);
    setFormData({ title:'', description:'', assigned_to:'', assigned_service_id:'', project_id:'', objectif_id:'', priority:'Moyenne', due_date:'', status:'À faire', resultat_attendu:'', assignType:'person', scheduled_date:'', scheduled_start_time:'', scheduled_end_time:'', vehicule_id:'' });
  };

  const toggleTaskExpand = (id) => setExpandedTasks(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev,id]);

  const isOverdue = (dueDate, status) => {
    if (!dueDate || ['Terminé','Annulée'].includes(status)) return false;
    return new Date(dueDate) < new Date();
  };

  // ── Filtering (rôle + filtres utilisateur) ──────────────────────────────
  const applyRoleFilter = (list) => {
    if (!currentUser) return [];
    if (['Patron','Super Manager'].includes(currentUser.role_name)) return list;
    const userId = parseInt(currentUser.employee_id || currentUser.id);
    if (currentUser.role_name === 'Manager') {
      // Multi-services : utiliser managerServiceIds
      const svcIds = managerServiceIds.length > 0 ? managerServiceIds : [parseInt(currentUser.service_id)];
      const svcEmps = employees.filter(e => svcIds.includes(parseInt(e.service_id))).map(e => e.id);
      return list.filter(t =>
        svcEmps.includes(parseInt(t.assigned_to)) ||
        parseInt(t.assigned_by) === userId ||
        svcIds.includes(parseInt(t.assigned_service_id))
      );
    }
    const userServiceId = parseInt(currentUser.service_id);
    return list.filter(t =>
      parseInt(t.assigned_to) === userId ||
      (userServiceId && parseInt(t.assigned_service_id) === userServiceId)
    );
  };

  const applyUserFilters = (list) => {
    let filtered = list;
    if (filterPerson !== 'all') {
      filtered = filtered.filter(t => String(t.assigned_to) === filterPerson);
    }
    if (filterService !== 'all') {
      filtered = filtered.filter(t => {
        if (String(t.assigned_service_id) === filterService) return true;
        const emp = employees.find(e => e.id === t.assigned_to);
        return emp && String(emp.service_id) === filterService;
      });
    }
    if (filterDate) {
      filtered = filtered.filter(t => {
        const dd = t.due_date ? t.due_date.split('T')[0] : null;
        const sd = t.scheduled_date || null;
        return dd === filterDate || sd === filterDate;
      });
    }
    return filtered;
  };

  const getSubtasks = (parentId) => tasks.filter(t => t.parent_task_id === parentId);
  const getFilteredEmployees = () => {
    if (!currentUser) return employees;
    if (['Patron','Super Manager'].includes(currentUser.role_name)) return employees;
    if (currentUser.role_name === 'Manager') {
      const svcIds = managerServiceIds.length > 0 ? managerServiceIds : [parseInt(currentUser.service_id)];
      return employees.filter(e => svcIds.includes(parseInt(e.service_id)));
    }
    return employees.filter(e => e.id === (currentUser.employee_id||currentUser.id));
  };

  const canManageTasks = () => currentUser && ['Patron','Super Manager','Manager'].includes(currentUser.role_name);
  const canSeeTeamView = () => currentUser && ['Patron','Super Manager','Manager'].includes(currentUser.role_name);

  // Hiérarchie des rôles pour le contrôle édition/suppression
  const ROLE_LEVEL = { 'Patron': 1, 'Super Manager': 2, 'Manager': 3, 'Employé': 4, 'Extra': 5 };
  const canEditTask = (task) => {
    if (!currentUser) return false;
    const myLevel = ROLE_LEVEL[currentUser.role_name] || 99;
    // Patron peut tout modifier
    if (myLevel === 1) return true;
    // Trouver le rôle du créateur (assigned_by)
    const creator = employees.find(e => e.id === parseInt(task.assigned_by));
    const creatorLevel = creator ? (ROLE_LEVEL[creator.role_name] || 99) : 99;
    // On peut éditer si le créateur a un niveau >= au nôtre (rôle inférieur ou égal)
    return creatorLevel >= myLevel;
  };

  // ── Kanban ──────────────────────────────────────────────────────────────
  const getFilteredTasksKanban = () => {
    if (!currentUser) return [];
    let filtered = tasks.filter(t => !t.parent_task_id);
    filtered = applyRoleFilter(filtered);
    filtered = applyUserFilters(filtered);
    if (filterType === 'standalone') filtered = filtered.filter(t => !t.project_id && !t.objectif_id);
    else if (filterType === 'project') filtered = filtered.filter(t => t.project_id);
    else if (filterType === 'objectif') filtered = filtered.filter(t => t.objectif_id);
    return filtered;
  };
  const filteredTasksKanban = getFilteredTasksKanban();

  const ALL_KANBAN_COLS = [
    { status: 'À faire',     label: 'À Faire',    bg: 'bg-elevated-1',   color: 'text-text-secondary',  badge: 'bg-gray-500' },
    { status: 'En cours',    label: 'En Cours',    bg: 'bg-blue-50 dark:bg-blue-950/40',    color: 'text-blue-700 dark:text-blue-300',  badge: 'bg-blue-600' },
    { status: 'Terminé',     label: 'Terminé',     bg: 'bg-green-50 dark:bg-green-950/40',   color: 'text-green-700 dark:text-green-300', badge: 'bg-green-600' },
    { status: 'En retard',   label: 'En Retard',   bg: 'bg-red-50 dark:bg-red-950/40',     color: 'text-red-700 dark:text-red-300',   badge: 'bg-red-600' },
    { status: 'Annulée',     label: 'Annulée',     bg: 'bg-orange-50 dark:bg-orange-950/40',  color: 'text-orange-700 dark:text-orange-300',badge: 'bg-orange-500' },
  ];

  const KANBAN_COLS = (() => {
    if (filterStatus === 'all') return ALL_KANBAN_COLS;
    if (filterStatus === 'active') return ALL_KANBAN_COLS.filter(c => !['Terminé','Annulée'].includes(c.status));
    if (filterStatus === 'done') return ALL_KANBAN_COLS.filter(c => c.status === 'Terminé');
    if (filterStatus === 'late') return ALL_KANBAN_COLS.filter(c => c.status === 'En retard');
    if (filterStatus === 'Annulée') return ALL_KANBAN_COLS.filter(c => c.status === 'Annulée');
    return ALL_KANBAN_COLS;
  })();

  const getTasksForKanban = (colStatus) => {
    if (colStatus === 'En retard') {
      return filteredTasksKanban.filter(t =>
        t.status === 'En retard' ||
        (isOverdue(t.due_date, t.status) &&
         !['En retard', 'Terminé', 'Annulée'].includes(t.status))
      );
    }
    if (colStatus === 'Terminé') return filteredTasksKanban.filter(t => t.status === 'Terminé');
    if (colStatus === 'Annulée') return filteredTasksKanban.filter(t => t.status === 'Annulée');
    return filteredTasksKanban.filter(t => t.status === colStatus && !isOverdue(t.due_date, t.status));
  };

  // ── Vue Calendrier perso : données ──────────────────────────────────────
  const calendarTasks = useMemo(() => {
    if (!currentUser) return [];
    let filtered = tasks.filter(t => !t.parent_task_id && t.scheduled_date === calendarDate && t.scheduled_start_time && t.scheduled_end_time && t.assigned_to);
    filtered = applyRoleFilter(filtered);
    if (filterPerson !== 'all') filtered = filtered.filter(t => String(t.assigned_to) === filterPerson);
    if (filterService !== 'all') {
      filtered = filtered.filter(t => {
        if (String(t.assigned_service_id) === filterService) return true;
        const emp = employees.find(e => e.id === t.assigned_to);
        return emp && String(emp.service_id) === filterService;
      });
    }
    return filtered;
  }, [tasks, calendarDate, currentUser, employees, filterPerson, filterService, managerServiceIds]);

  const calendarPersons = useMemo(() => {
    const ids = [...new Set(calendarTasks.map(t => t.assigned_to))];
    return ids.map(id => employees.find(e => e.id === id)).filter(Boolean);
  }, [calendarTasks, employees]);

  const filterableEmployees = useMemo(() => {
    if (!currentUser) return [];
    if (['Patron','Super Manager'].includes(currentUser.role_name)) return employees;
    if (currentUser.role_name === 'Manager') {
      const svcIds = managerServiceIds.length > 0 ? managerServiceIds : [parseInt(currentUser.service_id)];
      return employees.filter(e => svcIds.includes(parseInt(e.service_id)));
    }
    return employees.filter(e => e.id === (currentUser.employee_id || currentUser.id));
  }, [currentUser, employees, managerServiceIds]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const parseTime = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return h + (m || 0) / 60;
  };

  const navigateCalendar = (offset) => {
    const d = new Date(calendarDate);
    if (calendarSubView === 'semaine') d.setDate(d.getDate() + offset * 7);
    else d.setDate(d.getDate() + offset);
    setCalendarDate(d.toISOString().split('T')[0]);
  };

  const calendarDateFormatted = useMemo(() => {
    const d = new Date(calendarDate + 'T00:00:00');
    if (calendarSubView === 'semaine') {
      const dow = d.getDay();
      const monOff = dow === 0 ? -6 : 1 - dow;
      const mon = new Date(d); mon.setDate(d.getDate() + monOff);
      const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
      return `${mon.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} — ${sun.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }, [calendarDate, calendarSubView]);

  // Jours de la semaine pour la vue semaine calendrier perso
  const calendarWeekDays = useMemo(() => {
    const d = new Date(calendarDate + 'T00:00:00');
    const dow = d.getDay();
    const monOff = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(d); mon.setDate(d.getDate() + monOff);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(mon); day.setDate(mon.getDate() + i);
      return day.toISOString().split('T')[0];
    });
  }, [calendarDate]);

  // Tâches pour la vue semaine calendrier perso (toutes les tâches de la semaine)
  const calendarWeekTasks = useMemo(() => {
    if (!currentUser || calendarSubView !== 'semaine') return [];
    const days = calendarWeekDays;
    let filtered = tasks.filter(t => {
      if (t.parent_task_id) return false;
      if (!t.assigned_to) return false;
      // Tâche journalière
      if (t.scheduled_date && t.scheduled_start_time && t.scheduled_end_time) {
        return days.includes(t.scheduled_date);
      }
      // Tâche classique
      const dd = t.due_date ? t.due_date.split('T')[0] : null;
      return dd && days.includes(dd);
    });
    filtered = applyRoleFilter(filtered);
    if (filterPerson !== 'all') filtered = filtered.filter(t => String(t.assigned_to) === filterPerson);
    if (filterService !== 'all') {
      filtered = filtered.filter(t => {
        if (String(t.assigned_service_id) === filterService) return true;
        const emp = employees.find(e => e.id === t.assigned_to);
        return emp && String(emp.service_id) === filterService;
      });
    }
    return filtered;
  }, [tasks, calendarWeekDays, currentUser, calendarSubView, filterPerson, filterService, employees, managerServiceIds]);

  // Personnes visibles dans la vue semaine calendrier
  const calendarWeekPersons = useMemo(() => {
    const ids = [...new Set(calendarWeekTasks.map(t => t.assigned_to))];
    return ids.map(id => employees.find(e => e.id === id)).filter(Boolean);
  }, [calendarWeekTasks, employees]);

  const personColors = useMemo(() => {
    const map = {};
    calendarPersons.forEach((p, i) => { map[p.id] = PERSON_COLORS[i % PERSON_COLORS.length]; });
    return map;
  }, [calendarPersons]);

  // ══════════════════════════════════════════════════════════════════════════
  // VUE ÉQUIPE : helpers
  // ══════════════════════════════════════════════════════════════════════════
  const navigateTeam = (offset) => {
    const d = new Date(teamDate);
    if (teamSubView === 'jour') d.setDate(d.getDate() + offset);
    else if (teamSubView === 'semaine') d.setDate(d.getDate() + offset * 7);
    else d.setMonth(d.getMonth() + offset);
    setTeamDate(d.toISOString().split('T')[0]);
  };

  const teamDateFormatted = useMemo(() => {
    const d = new Date(teamDate + 'T00:00:00');
    if (teamSubView === 'jour') return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (teamSubView === 'semaine') {
      const dayOfWeek = d.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(d);
      monday.setDate(d.getDate() + mondayOffset);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return `${monday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} — ${sunday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  }, [teamDate, teamSubView]);

  // Employés visibles dans la vue équipe (filtrage multi-services pour Manager)
  const teamEmployees = useMemo(() => {
    if (!currentUser) return [];
    let emps = [];
    if (['Patron','Super Manager'].includes(currentUser.role_name)) {
      emps = employees.filter(e => !e.deleted_at && e.role_name !== 'Extra');
    } else if (currentUser.role_name === 'Manager') {
      const svcIds = managerServiceIds.length > 0 ? managerServiceIds : [parseInt(currentUser.service_id)];
      emps = employees.filter(e => svcIds.includes(parseInt(e.service_id)) && !e.deleted_at && e.role_name !== 'Extra');
    }
    // Appliquer filtre service si sélectionné
    if (filterService !== 'all') {
      emps = emps.filter(e => String(e.service_id) === filterService);
    }
    if (filterPerson !== 'all') {
      emps = emps.filter(e => String(e.id) === filterPerson);
    }
    return emps;
  }, [currentUser, employees, managerServiceIds, filterService, filterPerson]);

  // Tâches filtrées pour la vue équipe
  const teamTasks = useMemo(() => {
    if (!currentUser) return [];
    let filtered = tasks.filter(t => !t.parent_task_id);
    filtered = applyRoleFilter(filtered);
    return filtered;
  }, [tasks, currentUser, managerServiceIds, employees]);

  // Jours de la semaine (lundi → dimanche) pour la vue semaine
  const weekDays = useMemo(() => {
    const d = new Date(teamDate + 'T00:00:00');
    const dayOfWeek = d.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(d);
    monday.setDate(d.getDate() + mondayOffset);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      return day.toISOString().split('T')[0];
    });
  }, [teamDate]);

  // Jours du mois pour la vue mois
  const monthDays = useMemo(() => {
    const d = new Date(teamDate + 'T00:00:00');
    const year = d.getFullYear();
    const month = d.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    // Remplir les jours vides au début (lundi = 0)
    let startDow = firstDay.getDay();
    if (startDow === 0) startDow = 7;
    for (let i = 1; i < startDow; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dt = new Date(year, month, d);
      days.push(dt.toISOString().split('T')[0]);
    }
    return days;
  }, [teamDate]);

  // Obtenir les tâches d'un employé pour un jour donné
  const getTasksForEmployeeDay = (empId, dateStr) => {
    return teamTasks.filter(t => {
      if (parseInt(t.assigned_to) !== empId) return false;
      // Tâche journalière : scheduled_date correspond
      if (t.scheduled_date && t.scheduled_start_time && t.scheduled_end_time) {
        return t.scheduled_date === dateStr;
      }
      // Tâche classique : due_date correspond
      const dd = t.due_date ? t.due_date.split('T')[0] : null;
      return dd === dateStr;
    });
  };

  // Obtenir les tâches d'un jour (toutes personnes) pour la vue mois
  const getTasksForDay = (dateStr) => {
    const empIds = teamEmployees.map(e => e.id);
    return teamTasks.filter(t => {
      if (!empIds.includes(parseInt(t.assigned_to))) return false;
      if (t.scheduled_date && t.scheduled_start_time && t.scheduled_end_time) {
        return t.scheduled_date === dateStr;
      }
      const dd = t.due_date ? t.due_date.split('T')[0] : null;
      return dd === dateStr;
    });
  };

  // ── Stats ──
  const allTopLevel = filteredTasksKanban;
  const statuses = {
    'À faire': allTopLevel.filter(t => t.status === 'À faire').length,
    'En cours': allTopLevel.filter(t => t.status === 'En cours').length,
    'Terminé': allTopLevel.filter(t => t.status === 'Terminé').length,
    'En retard': allTopLevel.filter(t => t.status === 'En retard' || isOverdue(t.due_date, t.status)).length,
    'Annulée': allTopLevel.filter(t => t.status === 'Annulée').length,
  };

  // ── TaskCard ──────────────────────────────────────────────────────────────
  const TaskCard = ({ task, isSubtask = false }) => {
    const employee = employees.find(e => e.id === task.assigned_to);
    const overdue = isOverdue(task.due_date, task.status);
    const subtasks = getSubtasks(task.id);
    const isExpanded = expandedTasks.includes(task.id);
    const subtaskDone = subtasks.filter(s => s.status === 'Terminé').length;
    const statusStyle = getStatusStyle(task.status);
    return (
      <div className={`bg-surface rounded-lg shadow-sm border p-3 sm:p-4 hover:shadow-md transition border-l-4 ${isSubtask ? 'ml-3 sm:ml-4' : ''}`}
           style={{ borderLeftColor: task.priority === 'Critique' ? '#7c3aed' : task.priority === 'Haute' ? '#ef4444' : task.priority === 'Moyenne' ? '#f59e0b' : '#10b981' }}>
        <div>
          <div className="flex items-start justify-between mb-1.5">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              {subtasks.length > 0 && !isSubtask && (
                <button onClick={() => toggleTaskExpand(task.id)} className="mt-0.5 flex-shrink-0 hover:bg-elevated-1 rounded min-h-[44px] min-w-[44px] flex items-center justify-center sm:min-h-0 sm:min-w-0 sm:p-0.5">
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-text-muted" /> : <ChevronRight className="w-4 h-4 text-text-muted" />}
                </button>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-text-primary text-sm leading-tight">{task.title}</h4>
                <div className="flex flex-wrap gap-1 mt-1">
                  {task.project_name && (
                    <span className="inline-flex items-center text-xs bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded max-w-full">
                      <Folder className="w-3 h-3 mr-0.5 flex-shrink-0" /><span className="truncate">{task.project_name}</span>
                    </span>
                  )}
                  {task.objectif_titre && (
                    <span className="inline-flex items-center text-xs bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded max-w-full">
                      <Target className="w-3 h-3 mr-0.5 flex-shrink-0" /><span className="truncate">{task.objectif_titre}</span>
                    </span>
                  )}
                  {task.assigned_service_name && (
                    <span className="inline-flex items-center text-xs bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300 px-1.5 py-0.5 rounded max-w-full">
                      <UsersIcon className="w-3 h-3 mr-0.5 flex-shrink-0" /><span className="truncate">{task.assigned_service_name}</span>
                    </span>
                  )}
                  {task.vehicule_nom && (
                    <span className="inline-flex items-center text-xs bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 px-1.5 py-0.5 rounded max-w-full">
                      <Truck className="w-3 h-3 mr-0.5 flex-shrink-0" /><span className="truncate">{task.vehicule_nom}</span>
                    </span>
                  )}
                  {subtasks.length > 0 && (
                    <span className="inline-flex items-center text-xs bg-elevated-1 text-text-secondary px-1.5 py-0.5 rounded">
                      <Layers className="w-3 h-3 mr-0.5 flex-shrink-0" />{subtaskDone}/{subtasks.length}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {canEditTask(task) && (
              <div className="flex items-center gap-0.5 ml-2 flex-shrink-0">
                {!isSubtask && (
                  <button onClick={() => { setShowSubtaskModal(task.id); setShowAddModal(true); }} className="text-purple-600 hover:bg-purple-50 dark:bg-purple-950/40 min-h-[44px] min-w-[44px] flex items-center justify-center rounded" title="Sous-tâche">
                    <Plus className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => openEditModal(task)} className="text-blue-600 hover:bg-blue-50 dark:bg-blue-950/40 min-h-[44px] min-w-[44px] flex items-center justify-center rounded">
                  <Edit2 className="w-4 h-4" />
                </button>
                <button onClick={() => deleteTask(task.id)} className="text-red-600 hover:bg-red-50 dark:bg-red-950/40 min-h-[44px] min-w-[44px] flex items-center justify-center rounded">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {task.description && <p className="text-xs text-text-muted mb-1.5 line-clamp-2">{task.description}</p>}
          {task.resultat_attendu && <p className="text-xs text-indigo-600 dark:text-indigo-400 mb-1.5 italic truncate">→ {task.resultat_attendu}</p>}

          <div className="flex flex-col gap-1 text-xs mb-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {employee && <span className="flex items-center text-text-muted whitespace-nowrap"><User className="w-3 h-3 mr-1 flex-shrink-0" />{employee.first_name} {employee.last_name}</span>}
              {task.due_date && (
                <span className={`flex items-center whitespace-nowrap ${overdue ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-text-muted'}`}>
                  <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />{new Date(task.due_date).toLocaleDateString('fr-FR')}
                  {overdue && <AlertCircle className="w-3 h-3 ml-1 flex-shrink-0" />}
                </span>
              )}
              {task.scheduled_start_time && task.scheduled_end_time && (
                <span className="flex items-center text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                  <Clock className="w-3 h-3 mr-1 flex-shrink-0" />{task.scheduled_start_time} - {task.scheduled_end_time}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                task.priority === 'Critique' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300' :
                task.priority === 'Haute' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                task.priority === 'Moyenne' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' : 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
              }`}>{task.priority}</span>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium self-start ${statusStyle.badge}`}>
              {statusStyle.label}
            </span>
            <div className="flex flex-wrap gap-1">
              {task.status === 'À faire' && (
                <button onClick={() => changeStatus(task.id, 'En cours')}
                  className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 min-h-[44px] sm:min-h-0 sm:py-1 rounded">
                  Commencer
                </button>
              )}
              {task.status === 'En cours' && (
                <>
                  <button onClick={() => changeStatus(task.id, 'Terminé')}
                    className="text-xs bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 min-h-[44px] sm:min-h-0 sm:py-1 rounded flex items-center gap-0.5">
                    <Check className="w-3 h-3" />Terminer
                  </button>
                  <button onClick={() => changeStatus(task.id, 'En retard')}
                    className="text-xs bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:bg-red-800/40 text-red-700 dark:text-red-300 px-3 py-1.5 min-h-[44px] sm:min-h-0 sm:py-1 rounded">
                    Retard
                  </button>
                </>
              )}
              {task.status === 'En retard' && (
                <button onClick={() => changeStatus(task.id, 'En cours')}
                  className="text-xs bg-blue-100 dark:bg-blue-900/40 hover:bg-blue-200 dark:bg-blue-800/40 text-blue-700 dark:text-blue-300 px-3 py-1.5 min-h-[44px] sm:min-h-0 sm:py-1 rounded">
                  Reprendre
                </button>
              )}
              {!['À faire','Annulée'].includes(task.status) && (
                <button onClick={() => changeStatus(task.id, 'À faire')}
                  className="text-xs bg-elevated-1 hover:bg-elevated-1 text-text-secondary px-3 py-1.5 min-h-[44px] sm:min-h-0 sm:py-1 rounded">
                  Reset
                </button>
              )}
              {task.status !== 'Annulée' && canManageTasks() && (
                <button onClick={() => { if (window.confirm('Annuler cette tâche ?')) changeStatus(task.id, 'Annulée'); }}
                  className="text-xs bg-orange-100 dark:bg-orange-900/40 hover:bg-orange-200 dark:bg-orange-800/40 text-orange-700 dark:text-orange-300 px-3 py-1.5 min-h-[44px] sm:min-h-0 sm:py-1 rounded">
                  Annuler
                </button>
              )}
            </div>
          </div>
        </div>

        {isExpanded && subtasks.length > 0 && (
          <div className="border-t bg-elevated-1 p-2 pl-3 sm:pl-6 space-y-2">
            {subtasks.map(sub => <TaskCard key={sub.id} task={sub} isSubtask />)}
          </div>
        )}
      </div>
    );
  };

  // ── Checkbox tâche pour vue équipe ──────────────────────────────────────
  const TeamTaskRow = ({ task }) => {
    const isDone = task.status === 'Terminé';
    const overdue = isOverdue(task.due_date, task.status);
    const statusStyle = getStatusStyle(task.status);
    return (
      <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${isDone ? 'bg-green-50 dark:bg-green-950/40' : overdue ? 'bg-red-50 dark:bg-red-950/40' : 'bg-elevated-1'} group`}>
        <button
          onClick={(e) => { e.stopPropagation(); toggleTaskDone(task.id, task.status); }}
          className={`w-5 h-5 min-w-[20px] rounded border-2 flex items-center justify-center transition ${isDone ? 'bg-green-500 border-green-500 dark:border-green-500 text-white' : 'border-border-strong hover:border-blue-400 dark:border-blue-600'}`}
        >
          {isDone && <Check className="w-3 h-3" />}
        </button>
        <div className="flex-1 min-w-0">
          <span className={`font-medium truncate block ${isDone ? 'line-through text-text-muted' : overdue ? 'text-red-700 dark:text-red-300' : 'text-text-primary'}`}>
            {task.title}
          </span>
          {task.scheduled_start_time && task.scheduled_end_time && (
            <span className="text-[10px] text-text-muted">{task.scheduled_start_time}-{task.scheduled_end_time}</span>
          )}
        </div>
        {task.vehicule_nom && <Truck className="w-3 h-3 text-orange-500 flex-shrink-0" title={task.vehicule_nom} />}
        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusStyle.badge} flex-shrink-0`}>{statusStyle.label}</span>
        {canEditTask(task) && (
          <button onClick={() => openEditModal(task)} className="opacity-0 group-hover:opacity-100 text-blue-500 hover:bg-blue-50 dark:bg-blue-950/40 p-1 rounded">
            <Edit2 className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-surface rounded-xl shadow-lg p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Tâches</h1>
              <p className="text-text-muted text-xs sm:text-sm">Simple, liée à un projet ou objectif · 5 statuts</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap w-full sm:w-auto">
            {/* Bascule vue */}
            <div className="flex rounded-lg border overflow-hidden">
              <button onClick={() => setViewMode('kanban')}
                className={`flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium transition ${viewMode === 'kanban' ? 'bg-blue-600 text-white' : 'bg-surface text-text-secondary hover:bg-elevated-1'}`}>
                <LayoutGrid className="w-4 h-4" />Kanban
              </button>
              <button onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium transition ${viewMode === 'calendar' ? 'bg-blue-600 text-white' : 'bg-surface text-text-secondary hover:bg-elevated-1'}`}>
                <CalendarDays className="w-4 h-4" />Calendrier
              </button>
              {canSeeTeamView() && (
                <button onClick={() => setViewMode('team')}
                  className={`flex items-center gap-1.5 px-3 py-2 min-h-[44px] text-sm font-medium transition ${viewMode === 'team' ? 'bg-blue-600 text-white' : 'bg-surface text-text-secondary hover:bg-elevated-1'}`}>
                  <Eye className="w-4 h-4" />Équipe
                </button>
              )}
            </div>
            {canManageTasks() && (
              <button onClick={() => setShowAddModal(true)} className="min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg flex items-center justify-center gap-2 w-full sm:w-auto">
                <Plus className="w-5 h-5" /><span>Nouvelle Tâche</span>
              </button>
            )}
          </div>
        </div>

        {/* Filtres */}
        <div className="mt-3 pt-3 border-t flex flex-col sm:flex-row gap-2 flex-wrap">
          <div className="flex items-center gap-1.5 text-text-muted text-sm">
            <Filter className="w-4 h-4" />Filtres :
          </div>
          {viewMode !== 'team' && (
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="min-h-[44px] text-base sm:text-sm px-3 py-2 border rounded-lg w-full sm:w-auto">
              <option value="all">Toutes les tâches</option>
              <option value="standalone">Tâches simples</option>
              <option value="project">Liées à un projet</option>
              <option value="objectif">Liées à un objectif</option>
            </select>
          )}
          {viewMode === 'kanban' && (
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="min-h-[44px] text-base sm:text-sm px-3 py-2 border rounded-lg w-full sm:w-auto">
              <option value="all">Tous les statuts</option>
              <option value="active">En cours (actif)</option>
              <option value="late">En retard</option>
              <option value="done">Terminées</option>
              <option value="Annulée">Annulées</option>
            </select>
          )}
          <select value={filterPerson} onChange={e => setFilterPerson(e.target.value)} className="min-h-[44px] text-base sm:text-sm px-3 py-2 border rounded-lg w-full sm:w-auto">
            <option value="all">Toutes les personnes</option>
            {filterableEmployees.map(emp => (
              <option key={emp.id} value={String(emp.id)}>{emp.first_name} {emp.last_name}</option>
            ))}
          </select>
          <select value={filterService} onChange={e => setFilterService(e.target.value)} className="min-h-[44px] text-base sm:text-sm px-3 py-2 border rounded-lg w-full sm:w-auto">
            <option value="all">Tous les services</option>
            {servicesList.map(svc => (
              <option key={svc.id} value={String(svc.id)}>{svc.name}</option>
            ))}
          </select>
          {viewMode === 'kanban' && (
            <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
              className="min-h-[44px] text-base sm:text-sm px-3 py-2 border rounded-lg w-full sm:w-auto"
              title="Filtrer par date" />
          )}
          {(filterPerson !== 'all' || filterService !== 'all' || filterDate) && (
            <button onClick={() => { setFilterPerson('all'); setFilterService('all'); setFilterDate(''); }}
              className="min-h-[44px] text-sm text-red-600 hover:bg-red-50 dark:bg-red-950/40 px-3 py-2 rounded-lg border border-red-200 dark:border-red-800">
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      {/* ── VUE KANBAN ────────────────────────────────────────────────── */}
      {viewMode === 'kanban' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
            {Object.entries(statuses).map(([label, count]) => (
              <div key={label} className="bg-surface rounded-lg shadow p-3 sm:p-4">
                <p className="text-text-muted text-xs truncate">{label}</p>
                <p className={`text-2xl sm:text-3xl font-bold mt-1 ${
                  label === 'En retard' ? 'text-red-600 dark:text-red-400' :
                  label === 'En cours' ? 'text-blue-600 dark:text-blue-400' :
                  label === 'Terminé' ? 'text-green-600 dark:text-green-400' :
                  label === 'Annulée' ? 'text-orange-600 dark:text-orange-400' : 'text-text-secondary'
                }`}>{count}</p>
              </div>
            ))}
          </div>

          {/* Kanban board */}
          <div className={`grid grid-cols-1 gap-3 sm:gap-6 ${
            KANBAN_COLS.length === 1 ? 'md:grid-cols-1' :
            KANBAN_COLS.length === 2 ? 'md:grid-cols-2' :
            KANBAN_COLS.length === 3 ? 'md:grid-cols-3' :
            KANBAN_COLS.length === 4 ? 'md:grid-cols-4' : 'md:grid-cols-5'
          }`}>
            {KANBAN_COLS.map(col => {
              const colTasks = getTasksForKanban(col.status);
              return (
                <div key={col.status} className={`${col.bg} rounded-xl p-3 sm:p-4`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-bold ${col.color}`}>{col.label}</h3>
                    <span className={`${col.badge} text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold`}>
                      {colTasks.length}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {colTasks.map(task => <TaskCard key={task.id} task={task} />)}
                    {colTasks.length === 0 && <p className="text-center text-text-muted py-8 text-sm">Aucune tâche</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── VUE CALENDRIER PERSO ─────────────────────────────────────────── */}
      {viewMode === 'calendar' && (
        <div className="bg-surface rounded-xl shadow-lg overflow-hidden">
          <div className="flex flex-col sm:flex-row items-center justify-between p-3 sm:p-4 border-b bg-elevated-1 gap-2">
            <div className="flex items-center gap-2">
              <button onClick={() => navigateCalendar(-1)} className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-elevated-1 transition">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="text-center min-w-[200px]">
                <h2 className="text-base sm:text-lg font-bold text-text-primary capitalize">{calendarDateFormatted}</h2>
                <button onClick={() => setCalendarDate(new Date().toISOString().split('T')[0])}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline mt-0.5">Aujourd'hui</button>
              </div>
              <button onClick={() => navigateCalendar(1)} className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-elevated-1 transition">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div className="flex rounded-lg border overflow-hidden">
              <button onClick={() => setCalendarSubView('jour')}
                className={`px-3 py-2 min-h-[44px] text-sm font-medium transition ${calendarSubView === 'jour' ? 'bg-blue-600 text-white' : 'bg-surface text-text-secondary hover:bg-elevated-1'}`}>
                Jour
              </button>
              <button onClick={() => setCalendarSubView('semaine')}
                className={`px-3 py-2 min-h-[44px] text-sm font-medium transition ${calendarSubView === 'semaine' ? 'bg-blue-600 text-white' : 'bg-surface text-text-secondary hover:bg-elevated-1'}`}>
                Semaine
              </button>
            </div>
          </div>

          {/* ── Calendrier : VUE JOUR ── */}
          {calendarSubView === 'jour' && (
            calendarPersons.length === 0 ? (
              <div className="p-8 sm:p-12 text-center text-text-muted">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Aucune tâche planifiée pour cette journée</p>
                <p className="text-xs mt-1">Créez une tâche avec une date, heure de début et de fin pour la voir ici</p>
              </div>
            ) : (
              <>
                {/* Desktop grille */}
                <div className="hidden md:block overflow-x-auto">
                  <div className="min-w-[600px]" style={{ display: 'grid', gridTemplateColumns: `60px repeat(${calendarPersons.length}, minmax(160px, 1fr))` }}>
                    <div className="sticky top-0 bg-elevated-1 border-b border-r p-2 z-10" />
                    {calendarPersons.map(person => {
                      const col = personColors[person.id];
                      return (
                        <div key={person.id} className={`sticky top-0 ${col.header} text-white border-b p-2 sm:p-3 text-center z-10`}>
                          <p className="font-semibold text-sm truncate">{person.first_name} {person.last_name}</p>
                          <p className="text-xs opacity-80 truncate">{person.service_name || ''}</p>
                        </div>
                      );
                    })}
                    {CALENDAR_HOURS.map(hour => (
                      <React.Fragment key={hour}>
                        <div className="border-r border-b bg-elevated-1 p-1 text-right">
                          <span className="text-xs font-medium text-text-muted">{String(hour).padStart(2,'0')}:00</span>
                        </div>
                        {calendarPersons.map(person => {
                          const col = personColors[person.id];
                          const pTasks = calendarTasks.filter(t => t.assigned_to === person.id);
                          const hourTasks = pTasks.filter(t => {
                            const start = parseTime(t.scheduled_start_time);
                            const end = parseTime(t.scheduled_end_time);
                            return start < hour + 1 && end > hour;
                          });
                          return (
                            <div key={`${hour}-${person.id}`} className="border-b border-r relative" style={{ height: '72px' }}>
                              {hourTasks.map(t => {
                                const start = parseTime(t.scheduled_start_time);
                                const end = parseTime(t.scheduled_end_time);
                                const firstHour = Math.max(CALENDAR_HOURS[0], Math.floor(start));
                                if (hour !== firstHour) return null;
                                const durationHours = end - start;
                                const topOffset = (start - hour) * 72;
                                const sStyle = getStatusStyle(t.status);
                                return (
                                  <div key={t.id}
                                    className={`absolute left-1 right-1 ${col.bg} ${col.border} border-l-4 rounded-md px-3 py-1.5 overflow-hidden cursor-pointer hover:shadow-md transition z-10`}
                                    style={{ top: `${topOffset}px`, height: `${durationHours * 72}px`, minHeight: '44px' }}
                                    onClick={() => openEditModal(t)}
                                    title={`${t.title} (${t.scheduled_start_time} - ${t.scheduled_end_time})`}>
                                    <p className={`text-sm font-semibold ${col.text} truncate`}>{t.title}</p>
                                    <p className="text-xs text-text-muted mt-0.5">{t.scheduled_start_time} - {t.scheduled_end_time}</p>
                                    {t.vehicule_nom && <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5 truncate"><Truck className="w-3 h-3 inline mr-0.5" />{t.vehicule_nom}</p>}
                                    {durationHours >= 1 && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${sStyle.badge} mt-1 inline-block`}>{sStyle.label}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
                {/* Mobile liste */}
                <div className="md:hidden divide-y">
                  {calendarPersons.map(person => {
                    const col = personColors[person.id];
                    const pTasks = calendarTasks
                      .filter(t => t.assigned_to === person.id)
                      .sort((a, b) => parseTime(a.scheduled_start_time) - parseTime(b.scheduled_start_time));
                    return (
                      <div key={person.id} className="p-3">
                        <div className={`${col.header} text-white rounded-lg px-3 py-2 mb-2`}>
                          <p className="font-semibold text-sm">{person.first_name} {person.last_name}</p>
                          {person.service_name && <p className="text-xs opacity-80">{person.service_name}</p>}
                        </div>
                        <div className="space-y-1.5">
                          {pTasks.map(t => {
                            const sStyle = getStatusStyle(t.status);
                            const durationMin = Math.round((parseTime(t.scheduled_end_time) - parseTime(t.scheduled_start_time)) * 60);
                            return (
                              <div key={t.id}
                                className={`${col.bg} ${col.border} border-l-4 rounded-lg px-3 py-2.5 cursor-pointer active:opacity-80`}
                                onClick={() => openEditModal(t)}>
                                <div className="flex items-start justify-between">
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold ${col.text} truncate`}>{t.title}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs text-text-secondary font-medium">
                                        <Clock className="w-3 h-3 inline mr-0.5" />{t.scheduled_start_time} - {t.scheduled_end_time}
                                      </span>
                                      <span className="text-xs text-text-muted">{durationMin} min</span>
                                    </div>
                                    {t.vehicule_nom && <span className="text-xs text-orange-600 dark:text-orange-400"><Truck className="w-3 h-3 inline mr-0.5" />{t.vehicule_nom}</span>}
                                  </div>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${sStyle.badge} ml-2 flex-shrink-0`}>{sStyle.label}</span>
                                </div>
                              </div>
                            );
                          })}
                          {pTasks.length === 0 && (
                            <p className="text-center text-text-muted text-xs py-3">Pas de tâche planifiée</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )
          )}

          {/* ── Calendrier : VUE SEMAINE ── */}
          {calendarSubView === 'semaine' && (
            calendarWeekPersons.length === 0 ? (
              <div className="p-8 sm:p-12 text-center text-text-muted">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Aucune tâche planifiée cette semaine</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px] text-xs">
                  <thead>
                    <tr className="bg-elevated-1">
                      <th className="sticky left-0 bg-elevated-1 z-10 border-b border-r px-3 py-3 text-left font-semibold text-text-secondary w-40">Personne</th>
                      {calendarWeekDays.map(day => {
                        const d = new Date(day + 'T00:00:00');
                        const isToday = day === new Date().toISOString().split('T')[0];
                        return (
                          <th key={day} className={`border-b border-r px-2 py-3 text-center font-semibold min-w-[110px] ${isToday ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300' : 'text-text-secondary'}`}>
                            <div>{d.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                            <div className="text-sm">{d.getDate()}</div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {calendarWeekPersons.map((person, pIdx) => (
                      <tr key={person.id} className={pIdx % 2 === 0 ? 'bg-surface' : 'bg-elevated-1/50'}>
                        <td className="sticky left-0 bg-inherit z-10 border-b border-r px-3 py-2 font-medium text-text-primary whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${PERSON_COLORS[pIdx % PERSON_COLORS.length].header}`} />
                            <div>
                              <p className="truncate max-w-[120px]">{person.first_name} {person.last_name}</p>
                              <p className="text-[10px] text-text-muted truncate max-w-[120px]">{person.service_name}</p>
                            </div>
                          </div>
                        </td>
                        {calendarWeekDays.map(day => {
                          const isToday = day === new Date().toISOString().split('T')[0];
                          const dayTasks = calendarWeekTasks.filter(t => {
                            if (parseInt(t.assigned_to) !== person.id) return false;
                            if (t.scheduled_date && t.scheduled_start_time && t.scheduled_end_time) return t.scheduled_date === day;
                            const dd = t.due_date ? t.due_date.split('T')[0] : null;
                            return dd === day;
                          });
                          return (
                            <td key={day} className={`border-b border-r px-1.5 py-1.5 align-top ${isToday ? 'bg-blue-50/30' : ''}`}>
                              <div className="space-y-1 max-h-28 overflow-y-auto">
                                {dayTasks.map(t => {
                                  const isDone = t.status === 'Terminé';
                                  const overdue = t.due_date && new Date(t.due_date) < new Date() && !isDone && t.status !== 'Annulée';
                                  const sStyle = getStatusStyle(t.status);
                                  return (
                                    <div key={t.id} className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs cursor-pointer hover:shadow-sm ${isDone ? 'bg-green-50 dark:bg-green-950/40' : overdue ? 'bg-red-50 dark:bg-red-950/40' : 'bg-elevated-1'}`}
                                      onClick={() => openEditModal(t)}>
                                      <div className="flex-1 min-w-0">
                                        <span className={`font-medium truncate block ${isDone ? 'line-through text-text-muted' : overdue ? 'text-red-700 dark:text-red-300' : 'text-text-primary'}`}>
                                          {t.title}
                                        </span>
                                        {t.scheduled_start_time && t.scheduled_end_time && (
                                          <span className="text-[10px] text-text-muted">{t.scheduled_start_time}-{t.scheduled_end_time}</span>
                                        )}
                                      </div>
                                      {t.vehicule_nom && <Truck className="w-3 h-3 text-orange-500 flex-shrink-0" />}
                                      <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${sStyle.badge} flex-shrink-0`}>{sStyle.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* VUE ÉQUIPE (jour/semaine/mois) — Manager/Super Manager/Patron   */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {viewMode === 'team' && canSeeTeamView() && (
        <div className="bg-surface rounded-xl shadow-lg overflow-hidden">
          {/* ── Header responsive ── */}
          <div className="flex flex-col items-center p-3 sm:p-4 border-b bg-elevated-1 gap-2">
            <div className="flex items-center gap-1 w-full justify-center">
              <button onClick={() => navigateTeam(-1)} className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-elevated-1 transition">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-sm sm:text-lg font-bold text-text-primary capitalize text-center flex-1">{teamDateFormatted}</h2>
              <button onClick={() => navigateTeam(1)} className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg hover:bg-elevated-1 transition">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex rounded-lg border overflow-hidden">
                {['jour','semaine','mois'].map(v => (
                  <button key={v} onClick={() => setTeamSubView(v)}
                    className={`px-3 py-2 min-h-[44px] text-sm font-medium transition capitalize ${teamSubView === v ? 'bg-blue-600 text-white' : 'bg-surface text-text-secondary hover:bg-elevated-1'}`}>
                    {v}
                  </button>
                ))}
              </div>
              <button onClick={() => setTeamDate(new Date().toISOString().split('T')[0])}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap">Aujourd'hui</button>
            </div>
          </div>

          {teamEmployees.length === 0 ? (
            <div className="p-8 text-center text-text-muted">
              <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Aucun employé dans vos services</p>
            </div>
          ) : (
            <>
              {/* ══════════════════════════════════════════════════════════ */}
              {/* VUE JOUR                                                 */}
              {/* ══════════════════════════════════════════════════════════ */}
              {teamSubView === 'jour' && (
                <>
                  {/* Desktop : grille horaire */}
                  <div className="hidden md:block overflow-x-auto">
                    <div className="min-w-[600px]" style={{ display: 'grid', gridTemplateColumns: `60px repeat(${teamEmployees.length}, minmax(140px, 1fr))` }}>
                      <div className="sticky top-0 bg-elevated-1 border-b border-r p-2 z-10" />
                      {teamEmployees.map((emp, i) => {
                        const col = PERSON_COLORS[i % PERSON_COLORS.length];
                        return (
                          <div key={emp.id} className={`sticky top-0 ${col.header} text-white border-b p-2 text-center z-10`}>
                            <p className="font-semibold text-sm truncate">{emp.first_name} {emp.last_name}</p>
                            <p className="text-xs opacity-80 truncate">{emp.service_name || ''}</p>
                          </div>
                        );
                      })}
                      {CALENDAR_HOURS.map(hour => (
                        <React.Fragment key={hour}>
                          <div className="border-r border-b bg-elevated-1 p-1 text-right">
                            <span className="text-xs font-medium text-text-muted">{String(hour).padStart(2,'0')}:00</span>
                          </div>
                          {teamEmployees.map((emp, empIdx) => {
                            const col = PERSON_COLORS[empIdx % PERSON_COLORS.length];
                            const empTasks = getTasksForEmployeeDay(emp.id, teamDate).filter(t => t.scheduled_start_time && t.scheduled_end_time);
                            const hourTasks = empTasks.filter(t => {
                              const start = parseTime(t.scheduled_start_time);
                              const end = parseTime(t.scheduled_end_time);
                              return start < hour + 1 && end > hour;
                            });
                            return (
                              <div key={`${hour}-${emp.id}`} className="border-b border-r relative" style={{ height: '72px' }}>
                                {hourTasks.map(t => {
                                  const start = parseTime(t.scheduled_start_time);
                                  const end = parseTime(t.scheduled_end_time);
                                  const firstHour = Math.max(CALENDAR_HOURS[0], Math.floor(start));
                                  if (hour !== firstHour) return null;
                                  const durationHours = end - start;
                                  const topOffset = (start - hour) * 72;
                                  const isDone = t.status === 'Terminé';
                                  return (
                                    <div key={t.id}
                                      className={`absolute left-1 right-1 ${isDone ? 'bg-green-100 dark:bg-green-900/40 border-green-400 dark:border-green-600' : col.bg + ' ' + col.border} border-l-4 rounded-md px-2 py-1 overflow-hidden z-10`}
                                      style={{ top: `${topOffset}px`, height: `${durationHours * 72}px`, minHeight: '36px' }}>
                                      <div className="flex items-start gap-1">
                                        <button onClick={() => toggleTaskDone(t.id, t.status)}
                                          className={`w-4 h-4 min-w-[16px] mt-0.5 rounded border flex items-center justify-center ${isDone ? 'bg-green-500 border-green-500 dark:border-green-500 text-white' : 'border-gray-400 hover:border-blue-500 dark:border-blue-500'}`}>
                                          {isDone && <Check className="w-2.5 h-2.5" />}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-xs font-semibold truncate ${isDone ? 'line-through text-text-muted' : col.text}`}>{t.title}</p>
                                          <p className="text-[10px] text-text-muted">{t.scheduled_start_time}-{t.scheduled_end_time}</p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                    {/* Tâches classiques desktop */}
                    {(() => {
                      const classicTasks = teamEmployees.flatMap(emp =>
                        getTasksForEmployeeDay(emp.id, teamDate)
                          .filter(t => !(t.scheduled_start_time && t.scheduled_end_time))
                          .map(t => ({ ...t, _empName: `${emp.first_name} ${emp.last_name}` }))
                      );
                      if (classicTasks.length === 0) return null;
                      return (
                        <div className="border-t p-3 bg-elevated-1">
                          <h4 className="text-xs font-semibold text-text-secondary mb-2">Tâches de la journée (sans créneau horaire)</h4>
                          <div className="space-y-1">
                            {classicTasks.map(t => (
                              <div key={t.id} className="flex items-center gap-2 text-xs bg-surface rounded-lg px-3 py-2 border">
                                <button onClick={() => toggleTaskDone(t.id, t.status)}
                                  className={`w-5 h-5 min-w-[20px] rounded border-2 flex items-center justify-center ${t.status === 'Terminé' ? 'bg-green-500 border-green-500 dark:border-green-500 text-white' : 'border-border-strong hover:border-blue-400 dark:border-blue-600'}`}>
                                  {t.status === 'Terminé' && <Check className="w-3 h-3" />}
                                </button>
                                <span className={`font-medium ${t.status === 'Terminé' ? 'line-through text-text-muted' : isOverdue(t.due_date, t.status) ? 'text-red-700 dark:text-red-300' : 'text-text-primary'}`}>{t.title}</span>
                                <span className="text-text-muted ml-auto">{t._empName}</span>
                                {t.vehicule_nom && <Truck className="w-3 h-3 text-orange-500" title={t.vehicule_nom} />}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Mobile : liste verticale par employé */}
                  <div className="md:hidden divide-y">
                    {teamEmployees.map((emp, empIdx) => {
                      const col = PERSON_COLORS[empIdx % PERSON_COLORS.length];
                      const allEmpTasks = getTasksForEmployeeDay(emp.id, teamDate);
                      const scheduledTasks = allEmpTasks
                        .filter(t => t.scheduled_start_time && t.scheduled_end_time)
                        .sort((a, b) => (a.scheduled_start_time || '').localeCompare(b.scheduled_start_time || ''));
                      const classicTasks = allEmpTasks.filter(t => !(t.scheduled_start_time && t.scheduled_end_time));
                      return (
                        <div key={emp.id} className="p-3">
                          <div className={`${col.header} text-white rounded-lg px-3 py-2.5 mb-2`}>
                            <p className="font-semibold text-sm">{emp.first_name} {emp.last_name}</p>
                            {emp.service_name && <p className="text-xs opacity-80">{emp.service_name}</p>}
                          </div>
                          <div className="space-y-1.5">
                            {scheduledTasks.map(t => {
                              const isDone = t.status === 'Terminé';
                              const overdue = isOverdue(t.due_date, t.status);
                              const sStyle = getStatusStyle(t.status);
                              return (
                                <div key={t.id} className={`${isDone ? 'bg-green-50 dark:bg-green-950/40 border-green-300 dark:border-green-700' : col.bg + ' ' + col.border} border-l-4 rounded-lg px-3 py-2.5 flex items-center gap-2.5`}>
                                  <button onClick={() => toggleTaskDone(t.id, t.status)}
                                    className={`w-6 h-6 min-w-[24px] rounded border-2 flex items-center justify-center ${isDone ? 'bg-green-500 border-green-500 dark:border-green-500 text-white' : 'border-border-strong hover:border-blue-400 dark:border-blue-600'}`}>
                                    {isDone && <Check className="w-3.5 h-3.5" />}
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold ${isDone ? 'line-through text-text-muted' : overdue ? 'text-red-700 dark:text-red-300' : col.text}`}>{t.title}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      <span className="text-xs text-text-secondary font-medium">
                                        <Clock className="w-3 h-3 inline mr-0.5" />{t.scheduled_start_time} - {t.scheduled_end_time}
                                      </span>
                                      {t.vehicule_nom && <span className="text-xs text-orange-600 dark:text-orange-400"><Truck className="w-3 h-3 inline mr-0.5" />{t.vehicule_nom}</span>}
                                    </div>
                                  </div>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${sStyle.badge} flex-shrink-0`}>{sStyle.label}</span>
                                </div>
                              );
                            })}
                            {classicTasks.map(t => {
                              const isDone = t.status === 'Terminé';
                              const overdue = isOverdue(t.due_date, t.status);
                              const sStyle = getStatusStyle(t.status);
                              return (
                                <div key={t.id} className="bg-elevated-1 border-l-4 border-border-strong rounded-lg px-3 py-2.5 flex items-center gap-2.5">
                                  <button onClick={() => toggleTaskDone(t.id, t.status)}
                                    className={`w-6 h-6 min-w-[24px] rounded border-2 flex items-center justify-center ${isDone ? 'bg-green-500 border-green-500 dark:border-green-500 text-white' : 'border-border-strong hover:border-blue-400 dark:border-blue-600'}`}>
                                    {isDone && <Check className="w-3.5 h-3.5" />}
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-semibold ${isDone ? 'line-through text-text-muted' : overdue ? 'text-red-700 dark:text-red-300' : 'text-text-primary'}`}>{t.title}</p>
                                    <span className="text-xs text-text-muted italic">Journée</span>
                                  </div>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${sStyle.badge} flex-shrink-0`}>{sStyle.label}</span>
                                </div>
                              );
                            })}
                            {allEmpTasks.length === 0 && (
                              <p className="text-center text-text-muted text-xs py-3">Aucune tâche</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* ══════════════════════════════════════════════════════════ */}
              {/* VUE SEMAINE                                              */}
              {/* ══════════════════════════════════════════════════════════ */}
              {teamSubView === 'semaine' && (
                <>
                  {/* Desktop : tableau */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full min-w-[800px] text-xs">
                      <thead>
                        <tr className="bg-elevated-1">
                          <th className="sticky left-0 bg-elevated-1 z-10 border-b border-r px-3 py-3 text-left font-semibold text-text-secondary w-40">Employé</th>
                          {weekDays.map(day => {
                            const d = new Date(day + 'T00:00:00');
                            const isToday = day === new Date().toISOString().split('T')[0];
                            return (
                              <th key={day} className={`border-b border-r px-2 py-3 text-center font-semibold min-w-[110px] ${isToday ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300' : 'text-text-secondary'}`}>
                                <div>{d.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                                <div className="text-sm">{d.getDate()}</div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {teamEmployees.map((emp, empIdx) => (
                          <tr key={emp.id} className={empIdx % 2 === 0 ? 'bg-surface' : 'bg-elevated-1/50'}>
                            <td className="sticky left-0 bg-inherit z-10 border-b border-r px-3 py-2 font-medium text-text-primary whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${PERSON_COLORS[empIdx % PERSON_COLORS.length].header}`} />
                                <div>
                                  <p className="truncate max-w-[120px]">{emp.first_name} {emp.last_name}</p>
                                  <p className="text-[10px] text-text-muted truncate max-w-[120px]">{emp.service_name}</p>
                                </div>
                              </div>
                            </td>
                            {weekDays.map(day => {
                              const dayTasks = getTasksForEmployeeDay(emp.id, day);
                              const isToday = day === new Date().toISOString().split('T')[0];
                              return (
                                <td key={day} className={`border-b border-r px-1.5 py-1.5 align-top ${isToday ? 'bg-blue-50/30' : ''}`}>
                                  <div className="space-y-1 max-h-28 overflow-y-auto">
                                    {dayTasks.map(t => <TeamTaskRow key={t.id} task={t} />)}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile : accordéon par employé */}
                  <div className="md:hidden divide-y">
                    {teamEmployees.map((emp, empIdx) => {
                      const col = PERSON_COLORS[empIdx % PERSON_COLORS.length];
                      const isOpen = expandedWeekAccordion[emp.id] !== false; // ouvert par défaut
                      const empWeekTasks = weekDays.reduce((sum, day) => sum + getTasksForEmployeeDay(emp.id, day).length, 0);
                      return (
                        <div key={emp.id}>
                          <button
                            onClick={() => setExpandedWeekAccordion(prev => ({ ...prev, [emp.id]: !isOpen }))}
                            className="w-full flex items-center gap-3 px-3 py-3 min-h-[44px] hover:bg-elevated-1 transition">
                            <div className={`w-3 h-3 rounded-full ${col.header} flex-shrink-0`} />
                            <div className="flex-1 text-left min-w-0">
                              <p className="text-sm font-semibold text-text-primary truncate">{emp.first_name} {emp.last_name}</p>
                              <p className="text-xs text-text-muted truncate">{emp.service_name}</p>
                            </div>
                            {empWeekTasks > 0 && (
                              <span className="text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full px-2 py-0.5 font-bold flex-shrink-0">{empWeekTasks}</span>
                            )}
                            {isOpen ? <ChevronDown className="w-4 h-4 text-text-muted flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-text-muted flex-shrink-0" />}
                          </button>
                          {isOpen && (
                            <div className="px-3 pb-3 space-y-2">
                              {weekDays.map(day => {
                                const d = new Date(day + 'T00:00:00');
                                const isToday = day === new Date().toISOString().split('T')[0];
                                const dayTasks = getTasksForEmployeeDay(emp.id, day);
                                if (dayTasks.length === 0) return null;
                                return (
                                  <div key={day}>
                                    <p className={`text-xs font-bold mb-1 ${isToday ? 'text-blue-700 dark:text-blue-300' : 'text-text-secondary'}`}>
                                      {d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                                      {isToday && <span className="ml-1 text-blue-500 font-normal">(aujourd'hui)</span>}
                                    </p>
                                    <div className="space-y-1 pl-2 border-l-2 border-border-subtle">
                                      {dayTasks.map(t => {
                                        const isDone = t.status === 'Terminé';
                                        const overdue = isOverdue(t.due_date, t.status);
                                        const sStyle = getStatusStyle(t.status);
                                        return (
                                          <div key={t.id} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg ${isDone ? 'bg-green-50 dark:bg-green-950/40' : overdue ? 'bg-red-50 dark:bg-red-950/40' : 'bg-elevated-1'}`}>
                                            <button onClick={() => toggleTaskDone(t.id, t.status)}
                                              className={`w-6 h-6 min-w-[24px] rounded border-2 flex items-center justify-center ${isDone ? 'bg-green-500 border-green-500 dark:border-green-500 text-white' : 'border-border-strong hover:border-blue-400 dark:border-blue-600'}`}>
                                              {isDone && <Check className="w-3.5 h-3.5" />}
                                            </button>
                                            <div className="flex-1 min-w-0">
                                              <p className={`text-sm font-medium truncate ${isDone ? 'line-through text-text-muted' : overdue ? 'text-red-700 dark:text-red-300' : 'text-text-primary'}`}>{t.title}</p>
                                              {t.scheduled_start_time && t.scheduled_end_time && (
                                                <span className="text-xs text-text-muted">{t.scheduled_start_time}-{t.scheduled_end_time}</span>
                                              )}
                                            </div>
                                            {t.vehicule_nom && <Truck className="w-3 h-3 text-orange-500 flex-shrink-0" />}
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sStyle.badge} flex-shrink-0`}>{sStyle.label}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                              {empWeekTasks === 0 && (
                                <p className="text-xs text-text-muted text-center py-2">Aucune tâche cette semaine</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* ══════════════════════════════════════════════════════════ */}
              {/* VUE MOIS                                                 */}
              {/* ══════════════════════════════════════════════════════════ */}
              {teamSubView === 'mois' && (
                <div className="p-2 sm:p-3">
                  {/* En-têtes jours — abrégés 1 lettre sur mobile */}
                  <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1">
                    {[['L','Lun'],['M','Mar'],['M','Mer'],['J','Jeu'],['V','Ven'],['S','Sam'],['D','Dim']].map(([short, full], i) => (
                      <div key={i} className="text-center text-xs font-semibold text-text-muted py-1">
                        <span className="sm:hidden">{short}</span>
                        <span className="hidden sm:inline">{full}</span>
                      </div>
                    ))}
                  </div>
                  {/* Grille jours */}
                  <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                    {monthDays.map((day, i) => {
                      if (!day) return <div key={`empty-${i}`} className="min-h-[48px] sm:min-h-[80px]" />;
                      const d = new Date(day + 'T00:00:00');
                      const isToday = day === new Date().toISOString().split('T')[0];
                      const dayTasks = getTasksForDay(day);
                      const doneCount = dayTasks.filter(t => t.status === 'Terminé').length;
                      const overdueCount = dayTasks.filter(t => isOverdue(t.due_date, t.status)).length;
                      const pendingCount = dayTasks.length - doneCount - overdueCount;
                      return (
                        <div key={day}
                          className={`min-h-[48px] sm:min-h-[80px] border rounded-lg p-1 sm:p-1.5 cursor-pointer hover:bg-elevated-1 transition ${isToday ? 'border-blue-400 dark:border-blue-600 bg-blue-50/30' : 'border-border-subtle'}`}
                          onClick={() => { setTeamSubView('jour'); setTeamDate(day); }}>
                          <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                            <span className={`text-xs font-semibold ${isToday ? 'text-blue-700 dark:text-blue-300' : 'text-text-secondary'}`}>{d.getDate()}</span>
                            {dayTasks.length > 0 && (
                              <span className="text-[10px] bg-gray-200 text-text-secondary rounded-full px-1 sm:px-1.5">{dayTasks.length}</span>
                            )}
                          </div>
                          {/* Mobile : pastilles uniquement */}
                          <div className="sm:hidden flex flex-wrap gap-0.5 justify-center">
                            {doneCount > 0 && <span className="w-2.5 h-2.5 rounded-full bg-green-500" />}
                            {overdueCount > 0 && <span className="w-2.5 h-2.5 rounded-full bg-red-500" />}
                            {pendingCount > 0 && <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />}
                          </div>
                          {/* Desktop : texte des tâches */}
                          <div className="hidden sm:block space-y-0.5">
                            {dayTasks.slice(0, 3).map(t => (
                              <div key={t.id} className={`text-[10px] px-1 py-0.5 rounded truncate ${
                                t.status === 'Terminé' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 line-through' :
                                isOverdue(t.due_date, t.status) ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                                'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300'
                              }`}>
                                {t.title}
                              </div>
                            ))}
                            {dayTasks.length > 3 && (
                              <p className="text-[10px] text-text-muted text-center">+{dayTasks.length - 3} autres</p>
                            )}
                          </div>
                          {/* Indicateurs desktop */}
                          {(doneCount > 0 || overdueCount > 0) && (
                            <div className="hidden sm:flex gap-1 mt-1">
                              {doneCount > 0 && <span className="w-2 h-2 rounded-full bg-green-500" title={`${doneCount} terminée(s)`} />}
                              {overdueCount > 0 && <span className="w-2 h-2 rounded-full bg-red-500" title={`${overdueCount} en retard`} />}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* Modal Add/Edit/Subtask                                          */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative w-full sm:max-w-lg sm:mx-4 max-h-[90vh] sm:max-h-[85vh] bg-surface rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-2xl">
            <div className="sm:hidden flex justify-center pt-2 pb-1"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b bg-surface shrink-0">
              <h2 className="text-lg font-semibold text-text-primary">
                {showSubtaskModal ? 'Ajouter une sous-tâche' : editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-elevated-1 rounded-full">
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            <form onSubmit={showSubtaskModal ? handleSubmitSubtask : handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Titre *</label>
                <input required type="text" value={formData.title}
                  onChange={e => setFormData({...formData,title:e.target.value})}
                  className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm" placeholder="Nom de la tâche" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea rows="2" value={formData.description}
                  onChange={e => setFormData({...formData,description:e.target.value})}
                  className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm" />
              </div>

              {/* Assignment */}
              <div>
                <label className="block text-sm font-medium mb-1">Assigner à</label>
                <div className="flex gap-4 mb-2">
                  {['person','service'].map(t => (
                    <label key={t} className="flex items-center gap-2 cursor-pointer min-h-[44px]">
                      <input type="radio" checked={formData.assignType === t}
                        onChange={() => setFormData({...formData, assignType:t, ...(t==='person'?{assigned_service_id:''}:{assigned_to:''})})} className="w-5 h-5 sm:w-4 sm:h-4" />
                      <span className="text-sm">{t === 'person' ? 'Une personne' : 'Un service'}</span>
                    </label>
                  ))}
                </div>
                {formData.assignType === 'person' ? (
                  <select value={formData.assigned_to} onChange={e => setFormData({...formData,assigned_to:e.target.value})} className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm">
                    <option value="">Sélectionner un employé</option>
                    {(() => {
                      const all = getFilteredEmployees();
                      const staff = all.filter(e => e.role_name !== 'Extra');
                      const extras = all.filter(e => e.role_name === 'Extra');
                      return (
                        <>
                          <optgroup label="Équipe">
                            {staff.map(emp => <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name} — {emp.role_name}</option>)}
                          </optgroup>
                          {extras.length > 0 && (
                            <optgroup label="Extras">
                              {extras.map(emp => <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>)}
                            </optgroup>
                          )}
                        </>
                      );
                    })()}
                  </select>
                ) : (
                  <select value={formData.assigned_service_id} onChange={e => setFormData({...formData,assigned_service_id:e.target.value})} className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm">
                    <option value="">Sélectionner un service</option>
                    {servicesList.map(svc => <option key={svc.id} value={svc.id}>{svc.name}</option>)}
                  </select>
                )}
              </div>

              {/* Links (not for subtasks) */}
              {!showSubtaskModal && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Projet (optionnel)</label>
                    <select value={formData.project_id} onChange={e => setFormData({...formData,project_id:e.target.value})} className="w-full px-3 py-2.5 sm:py-2 border rounded-lg text-base sm:text-sm min-h-[44px]">
                      <option value="">Aucun</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Objectif (optionnel)</label>
                    <select value={formData.objectif_id} onChange={e => setFormData({...formData,objectif_id:e.target.value})} className="w-full px-3 py-2.5 sm:py-2 border rounded-lg text-base sm:text-sm min-h-[44px]">
                      <option value="">Aucun</option>
                      {objectifs.map(o => <option key={o.id} value={o.id}>{o.titre}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Priorité</label>
                  <select value={formData.priority} onChange={e => setFormData({...formData,priority:e.target.value})} className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm">
                    {['Faible','Moyenne','Haute','Critique'].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date limite *</label>
                  <input type="date" required value={formData.due_date} onChange={e => setFormData({...formData,due_date:e.target.value})} className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm" />
                </div>
              </div>

              {/* Véhicule */}
              <div>
                <label className="block text-sm font-medium mb-1 flex items-center gap-1.5">
                  <Truck className="w-4 h-4 text-orange-500" />Véhicule (optionnel)
                </label>
                <select value={formData.vehicule_id} onChange={e => setFormData({...formData,vehicule_id:e.target.value})} className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm">
                  <option value="">Aucun véhicule</option>
                  {vehicules.map(v => <option key={v.id} value={v.id}>{v.nom} — {v.immatriculation}</option>)}
                </select>
              </div>

              {/* Alerte conflit véhicule */}
              {vehicleAlert && vehicleAlert.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-300 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Conflit véhicule détecté</p>
                      {vehicleAlert.map(c => (
                        <p key={c.id} className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                          Le véhicule est assigné à « {c.title} »
                          {c.scheduled_start_time && c.scheduled_end_time
                            ? ` de ${c.scheduled_start_time} à ${c.scheduled_end_time}`
                            : ` (prévu pour la journée)`}
                          {c.assigned_to_name && ` — ${c.assigned_to_name}`}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Planification horaire */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold text-text-secondary mb-2 flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4 text-indigo-500" />Planification horaire (tâche journalière)
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Date planifiée</label>
                    <input type="date" value={formData.scheduled_date} onChange={e => setFormData({...formData, scheduled_date:e.target.value})}
                      className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Heure début</label>
                    <input type="time" value={formData.scheduled_start_time} onChange={e => setFormData({...formData, scheduled_start_time:e.target.value})}
                      className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-text-secondary mb-1">Heure fin</label>
                    <input type="time" value={formData.scheduled_end_time} onChange={e => setFormData({...formData, scheduled_end_time:e.target.value})}
                      className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm" />
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-1">Remplissez ces 3 champs pour une tâche journalière. Le planning de l'employé sera vérifié automatiquement.</p>
              </div>

              {!showSubtaskModal && (
                <div>
                  <label className="block text-sm font-medium mb-1">Résultat attendu</label>
                  <input type="text" value={formData.resultat_attendu} onChange={e => setFormData({...formData,resultat_attendu:e.target.value})}
                    className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm" placeholder="Ex: 100 contacts, 5 000 € CA…" />
                </div>
              )}

              {(editingTask && !showSubtaskModal) && (
                <div>
                  <label className="block text-sm font-medium mb-1">Statut</label>
                  <select value={formData.status} onChange={e => setFormData({...formData,status:e.target.value})} className="w-full px-3 py-2.5 sm:py-2 border rounded-lg min-h-[44px] text-base sm:text-sm">
                    {TASK_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              )}
              </div>

              <div className="shrink-0 border-t bg-surface px-4 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pb-safe">
                <button type="button" onClick={closeModal} className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium text-center">Annuler</button>
                <button type="submit" disabled={loading} className="w-full sm:w-auto min-h-[44px] px-4 py-3 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 text-center">
                  {showSubtaskModal ? 'Ajouter sous-tâche' : editingTask ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Taches;
